import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { shippingService } from '../services/shipping.service'
import type { CreateShipmentPayload } from '../types/shipping.types'

export const shippingKeys = {
  all: ['shipping'] as const,
  tracking: (awb: string) => [...shippingKeys.all, 'tracking', awb] as const,
}

export function useShipmentTracking(awb: string | undefined | null) {
  return useQuery({
    queryKey: shippingKeys.tracking(awb || ''),
    queryFn: () => shippingService.getTracking(awb!),
    enabled: Boolean(awb && awb.trim().length > 0),
    refetchInterval: 30000, // Auto-refresh tracking every 30 seconds
  })
}

export function useCreateShipment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ orderId, payload }: { orderId: number; payload?: CreateShipmentPayload }) =>
      shippingService.createShipment(orderId, payload),
    onSuccess: (data) => {
      toast.success(data.message || 'Shipment booked via Bigship successfully!')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: shippingKeys.all })
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to book shipment on Bigship')
    },
  })
}

export function useAdvanceShipmentStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      orderId,
      status,
      location,
      activity,
    }: {
      orderId: number
      status: string
      location?: string
      activity?: string
    }) => shippingService.advanceStatus(orderId, { status, location, activity }),
    onSuccess: (_, vars) => {
      toast.success(`Shipment milestone updated to ${vars.status}`)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: shippingKeys.all })
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to update milestone')
    },
  })
}
